import { createApp } from 'vue'
import { createPinia } from 'pinia'
import { createRouter, createWebHistory } from 'vue-router'
import { createVuetify } from 'vuetify'
import * as components from 'vuetify/components'
import * as directives from 'vuetify/directives'
import { registerSW } from 'virtual:pwa-register'
import { createI18n } from 'vue-i18n'
import en from './locales/en.json'

// Styles
import './styles/global.css'
import 'vuetify/styles'
import '@mdi/font/css/materialdesignicons.css'
import fontOptimization from './utils/fontOptimization'
import cookieConsent from './utils/cookieConsent'

// Import config service for global initialization
import { configService } from './utils/configService'

// App component
import App from './App.vue'
import PrivacyPage from './pages/PrivacyPage.vue'
import SupportPage from './pages/SupportPage.vue'
import { pickRoot } from './router/rootByPath'

// Define routes. App.vue does not host a <router-view>, so non-root routes
// are realised by selecting the mounted root via pickRoot() below rather
// than via <router-view> projection. See router/rootByPath.ts.
//
// /privacy and /support are served as static files in public/ — the entries
// here are dead but harmless since CloudFront/Vite serve the physical file
// before the SPA fallback runs.
const routes = [
  {
    path: '/',
    component: App
  },
  { path: '/privacy', component: PrivacyPage },
  { path: '/support', component: SupportPage }
]

// Create router
const router = createRouter({
  history: createWebHistory('/'),
  routes
})

// Create Vuetify instance
const vuetify = createVuetify({
  components,
  directives,
  theme: {
    defaultTheme: 'light',
    themes: {
      dark: {
        dark: true,
        colors: {
          background: '#000000',
          surface: '#000000',
          primary: '#2196F3',
          secondary: '#424242',
          accent: '#2196F3',
          error: '#ff5252',
          info: '#2196F3',
          success: '#4CAF50',
          warning: '#FB8C00'
        }
      },
      light: {
        dark: false,
        colors: {
          background: '#fff1e5',  // FT Paper - main background
          surface: '#fff1e5',
          primary: '#0d7680',     // FT Teal - main CTA color
          secondary: '#4d4845',   // FT black-70 - support text
          accent: '#0d7680',      // FT Teal
          error: '#cc0000',       // FT Crimson
          info: '#0d7680',
          success: '#00994d',     // FT Jade
          warning: '#ff8833',     // FT Mandarin
          'on-background': '#33302e',  // FT black-80 - body text
          'on-surface': '#33302e'
        }
      }
    }
  }
})

// Create Pinia store
const pinia = createPinia()

// Register service worker
const updateSW = registerSW({
  onNeedRefresh() {
    // Logic to prompt user to refresh
  },
  onOfflineReady() {
    // Logic to notify user the app is ready for offline use
  }
})

// Import translation service
import { detectUserLanguage, useTranslationService } from './utils/translationService'

// Function to sanitize translation messages
function sanitizeMessages(messages: any): any {
  if (messages === null || typeof messages !== 'object') {
    return messages
  }

  return Object.keys(messages).reduce((acc, key) => {
    const value = messages[key]
    if (typeof value === 'string') {
      acc[key] = value.replace(/{年度}/g, '{year}')
    } else {
      acc[key] = sanitizeMessages(value)
    }
    return acc
  }, {} as any)
}

// Detect user's preferred language
const userLanguage = detectUserLanguage()

const i18n = createI18n({
  legacy: false,
  locale: userLanguage,
  fallbackLocale: 'en',
  messages: { en: sanitizeMessages(en) }, // Sanitize default English messages too
})

// Initialize font optimization for public Wi-Fi networks
fontOptimization.initialize().catch(error => {
  console.warn('Font optimization initialization failed:', error)
})

// Initialize cookie consent for GDPR compliance
cookieConsent.initialize(userLanguage).catch(error => {
  console.warn('Cookie consent initialization failed:', error)
})

// Update loading screen theme - always use light mode
function updateLoadingScreenTheme() {
  const loadingScreen = document.getElementById('app-loading-screen')
  if (loadingScreen) {
    // Always use light mode for loading screen
    loadingScreen.style.backgroundColor = '#fff1e5' // FT Paper - always light
    const svg = loadingScreen.querySelector('svg')
    if (svg) {
      svg.style.color = 'rgba(51, 48, 46, 0.87)' // Dark text for light background
    }
  }
}

// Remove loading screen
function hideLoadingScreen() {
  const loadingScreen = document.getElementById('app-loading-screen')
  if (loadingScreen) {
    loadingScreen.style.opacity = '0'
    loadingScreen.style.transition = 'opacity 0.3s ease'
    setTimeout(() => {
      loadingScreen.remove()
    }, 300)
  }
}

// MAIN INITIALIZATION: Load config FIRST, before anything else
async function initializeApp() {
  try {
    // Loading screen is already shown in index.html
    // Update its theme once CSS variables are available
    updateLoadingScreenTheme()
    
    console.log('🚀 Starting app initialization...')
    
    // STEP 1: Load global configuration, but do NOT make it a
    // prerequisite for mounting (#186).
    //
    // /api/config is a pass-through to SWIM. Treating its failure as
    // fatal meant one upstream blip white-screened the entire app —
    // including every screen that needs nothing from SWIM (Pro charts,
    // Settings, About, Location). On dev, where SWIM is deliberately
    // left stopped, it made the app permanently unusable.
    //
    // Consumers already degrade on their own: Settings falls back to
    // DEFAULT_LLM, Forecast guards on getConfig() and retries. The
    // right failure is a feature that says so, not a blank page.
    console.log('🔧 Loading global configuration...')
    try {
      await configService.loadConfig()
      console.log('✅ Global configuration loaded successfully')
    } catch (error) {
      console.warn(
        '⚠️ Global configuration unavailable — continuing without it. ' +
          'Features backed by SWIM will degrade; the rest of the app works.',
        error
      )
    }

    // STEP 2: Create the Vue app.
    // App.vue has no <router-view>, so any SPA-routed page would have to
    // be mounted as the root component via pickRoot(). Currently no
    // non-default routes — pickRoot() always resolves to App.vue.
    console.log('🔧 Creating Vue app...')
    const app = createApp(pickRoot(window.location.pathname, App))
    app.use(router)
    app.use(pinia)
    app.use(vuetify)
    app.use(i18n)
    
    // Make i18n globally accessible for translation service
    ;(window as any).$i18n = i18n
    
    // STEP 3: Initialize translation service
    console.log('🔧 Initializing translation service...')
    const translationService = useTranslationService(i18n)
    
    // Load translations for detected language if not English
    if (userLanguage !== 'en' && translationService) {
      await translationService.loadTranslations(userLanguage).catch(error => {
        console.warn('Failed to load initial translations:', error)
        // Fallback to English
        i18n.global.locale.value = 'en'
      })
    }
    
    // STEP 4: Mount the app.
    // router.isReady() resolves once the initial navigation completes.
    // Kept defensively for any future root-mounted page (no <router-view>
    // above it) that needs route.query/params on its first paint.
    console.log('🔧 Mounting Vue app...')
    await router.isReady()
    app.mount('#app')
    console.log('✅ App initialization complete!')

    // Initialize auth: refresh token and fetch profile if logged in
    const { useAuthStore } = await import('@/stores/useAuthStore')
    const authStore = useAuthStore()
    if (authStore.token) {
      await authStore.refresh()
      await authStore.fetchProfile()
    }
    
    // Update loading screen theme again after app is mounted (theme might have changed)
    updateLoadingScreenTheme()
    
    // Hide loading screen after a short delay to ensure app is rendered
    setTimeout(() => {
      hideLoadingScreen()
    }, 100)

  } catch (error) {
    console.error('❌ App initialization failed:', error)
    
    // Create a minimal error display
    const errorDiv = document.createElement('div')
    errorDiv.style.cssText = `
      position: fixed;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      background: #f44336;
      color: white;
      padding: 20px;
      border-radius: 8px;
      font-family: Arial, sans-serif;
      text-align: center;
      z-index: 9999;
    `
    errorDiv.innerHTML = `
      <h3>⚠️ App Failed to Load</h3>
      <p>Configuration could not be loaded. Please refresh the page.</p>
      <button onclick="location.reload()" style="padding: 10px 20px; margin-top: 10px; border: none; border-radius: 4px; background: white; color: #f44336; cursor: pointer;">Refresh Page</button>
    `
    document.body.appendChild(errorDiv)
  }
}

// Start the initialization process
initializeApp()
